import { useMemo } from 'react';

import {
  MarketAccountStatusEnum,
  useCurrenciesContext,
  useMarketAccountsContext,
  useMarketsContext,
} from '@talos/kyoko';

import {
  getTransferBalanceDescription,
  getTransferOptionLabel,
  type BalancesByCurrencyMarketAccountID,
} from '@src/containers/Transfers/transferOptionUtils';
import type { SourceDestinationOption } from '@src/containers/Transfers/types';
import type { CustomerBalanceTransactionDrawerState } from '../types';

interface UseTransferOptionsProps {
  drawerState: CustomerBalanceTransactionDrawerState;
  balancesByCurrencyMarketAccountID?: BalancesByCurrencyMarketAccountID;
}

export function useTransferOptions({ drawerState, balancesByCurrencyMarketAccountID }: UseTransferOptionsProps) {
  const { marketAccountsList } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();
  const { currenciesBySymbol } = useCurrenciesContext();
  const defaultIncrement = currenciesBySymbol.get(drawerState.aliasCurrency ?? '')?.DefaultIncrement;

  // Convert market accounts to SourceDestinationOption format
  const sourceOptions = useMemo(() => {
    if (!marketAccountsList) {
      return [];
    }

    return marketAccountsList
      .filter(account => {
        // Filter by provider if selected
        if (drawerState.provider && account.Market !== drawerState.provider) {
          return false;
        }
        // Only include active accounts
        if (account.Status !== MarketAccountStatusEnum.Active) {
          return false;
        }
        return true;
      })
      .map(
        account =>
          ({
            marketAccount: account,
            label: getTransferOptionLabel(account, marketsByName?.get(account.Market)),
            description: getTransferBalanceDescription(
              account.MarketAccountID,
              drawerState.aliasCurrency,
              balancesByCurrencyMarketAccountID,
              defaultIncrement
            ),
            addressForCurrency: drawerState.currency,
          }) as SourceDestinationOption
      );
  }, [
    marketAccountsList,
    marketsByName,
    drawerState.provider,
    drawerState.currency,
    drawerState.aliasCurrency,
    balancesByCurrencyMarketAccountID,
    defaultIncrement,
  ]);

  const destinationOptions = useMemo(() => {
    if (!marketAccountsList) {
      return [];
    }

    return marketAccountsList
      .filter(account => {
        // Filter by provider if selected
        if (drawerState.provider && account.Market !== drawerState.provider) {
          return false;
        }
        // Only include active accounts
        if (account.Status !== MarketAccountStatusEnum.Active) {
          return false;
        }
        return true;
      })
      .map(
        account =>
          ({
            marketAccount: account,
            label: getTransferOptionLabel(account, marketsByName?.get(account.Market)),
            description: getTransferBalanceDescription(
              account.MarketAccountID,
              drawerState.aliasCurrency,
              balancesByCurrencyMarketAccountID,
              defaultIncrement,
              account
            ),
            addressForCurrency: drawerState.currency,
          }) as SourceDestinationOption
      );
  }, [
    marketAccountsList,
    marketsByName,
    drawerState.provider,
    drawerState.currency,
    drawerState.aliasCurrency,
    balancesByCurrencyMarketAccountID,
    defaultIncrement,
  ]);

  return { sourceOptions, destinationOptions };
}
